// A pending question on a run (docs/07 human-in-the-loop). Its text was decrypted in this
// browser; the answer, an offered option or free text, is encrypted and signed in the
// browser, sent over the device socket, and only then recorded against the question
// through the API.
import { CheckCircle, LockKey, PaperPlaneRight, Question as QuestionIcon } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import { Button, Input } from "rizzui";

import type { Question } from "../api/client";
import { answerQuestion } from "../api/queries";
import { answerContent } from "../conversation/model";
import type { AskedQuestion } from "../conversation/question";
import type { ConversationHandle } from "../conversation/useConversation";
import { messages, useT } from "../i18n";
import { toast } from "../layout/toast";
import { hourMinute } from "../workloads/format";

/** Whether this browser can read and answer the question. */
export type QuestionAccess =
  | { kind: "unlinked" }
  | { kind: "no-conversation" }
  | { kind: "linked"; deviceId: string; send: ConversationHandle["send"] };

interface Props {
  question: Question;
  /** The decrypted question, once this browser holds its event and key. */
  asked: AskedQuestion | undefined;
  access: QuestionAccess;
  /** Where the run is: "main · icykofi". */
  where?: string;
}

const card =
  "relative overflow-hidden rounded-xl border border-orange/40 bg-gradient-to-br from-orange-lighter/70 via-gray-0 to-gray-0 p-5 dark:from-orange-lighter/50 dark:via-gray-50 dark:to-gray-50";

export function QuestionCard({ question, asked, access, where }: Props) {
  const { common, conversation } = useT();
  const t = conversation.question;
  const approval = question.kind === "approval";
  return (
    <section className={card} data-testid="question" data-question-id={question.id} data-status={question.status} aria-label={t.label}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange text-white dark:text-gray-0">
          <QuestionIcon weight="fill" className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 basis-[calc(100%-44px)] sm:basis-0">
          <div className="text-[13px] font-semibold text-gray-900">{approval ? t.needsApproval : t.waiting}</div>
          <div className="text-xs text-gray-500">
            {[where, t.asked(hourMinute(question.created_at)), t.checkpointed].filter(Boolean).join(" · ")}
          </div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-lighter px-2 py-0.5 text-[11px] font-medium text-secondary-dark">
          <LockKey weight="fill" className="h-3 w-3" /> {asked ? t.decryptedHere : common.e2ee}
        </span>
      </div>
      <QuestionBody question={question} asked={asked} access={access} />
    </section>
  );
}

function QuestionBody({ question, asked, access }: Omit<Props, "where">) {
  const { conversation } = useT();
  const t = conversation.question;
  switch (access.kind) {
    case "unlinked":
      return (
        <p className="mb-0 mt-4 text-[13.5px] text-gray-700">{t.unlinked(<Link to="/devices">{conversation.linkThisBrowser}</Link>)}</p>
      );
    case "no-conversation":
      return <p className="mb-0 mt-4 text-[13.5px] text-gray-700">{t.noConversation}</p>;
    case "linked":
      return <AnswerForm question={question} asked={asked} deviceId={access.deviceId} send={access.send} />;
  }
}

function AnswerForm({
  question,
  asked,
  deviceId,
  send,
}: {
  question: Question;
  asked: AskedQuestion | undefined;
  deviceId: string;
  send: ConversationHandle["send"];
}) {
  const t = useT().conversation.question;
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState<string>();

  async function answer(value: string) {
    if (value.trim() === "") return;
    setBusy(true);
    try {
      const result = await send({ eventType: "answer", text: answerContent(question.id, value) });
      await answerQuestion(question.id, deviceId);
      setAnswered(value);
      await queryClient.invalidateQueries({ queryKey: ["runs", question.run_id, "questions"] });
      setText("");
      const done = messages().conversation.question;
      toast(result.delivered ? done.delivered : done.queued);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), "warn");
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void answer(text);
  }

  return (
    <>
      <p className="mb-0 mt-4 font-display text-[17px] font-semibold leading-snug text-gray-900">
        {asked?.text ?? t.decrypting}
      </p>
      {answered !== undefined ? (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-green-lighter px-3 py-2.5 text-[13px] text-green-dark">
          <CheckCircle weight="fill" className="h-5 w-5 shrink-0" />
          <span>{t.answered(answered)}</span>
        </div>
      ) : (
        <>
          {asked && asked.options.length > 0 ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {asked.options.map((o) => (
                <Button
                  key={o}
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  className="rounded-full border-gray-300 bg-gray-0 hover:border-primary hover:text-primary dark:bg-gray-100"
                  onClick={() => void answer(o)}
                >
                  {o}
                </Button>
              ))}
            </div>
          ) : null}
          <form className="mt-3 flex gap-2" aria-label={t.form} onSubmit={onSubmit}>
            <Input
              className="min-w-0 flex-1"
              inputClassName="bg-gray-0 dark:bg-gray-100"
              aria-label={t.yourAnswer}
              placeholder={asked && asked.options.length > 0 ? t.ownAnswer : t.typeAnswer}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={busy}
            />
            <Button type="submit" className="shrink-0 gap-1.5" disabled={busy || text.trim() === ""}>
              <PaperPlaneRight weight="fill" className="h-4 w-4" /> {busy ? t.sending : t.answer}
            </Button>
          </form>
          <p className="mb-0 mt-3 text-[11.5px] text-gray-500">
            {t.footnote}
          </p>
        </>
      )}
    </>
  );
}
